import { useEffect, useState, useRef, useContext } from 'react';
import { Option, Tooltip } from '@fluentui/react-components';
import {
  Attitude,
  EmployeeSkillsByCategoryDTO,
  Level,
  OptionSet,
} from 'src/tabs/models/SkillsInterfaces';
import {
  SkillsAddPopupOverlay,
  SkillsMiddleContainer,
  RadioGroupStyled,
  FinishingActionsContainer,
  DropdownStyledPopup,
  SelectASkillImageStyled,
  ComboboxStyle,
  OptionsWrapper,
} from './styles/SkillsAddPopup.styled';
import { useCreateSkill } from '../hooks/useCreateSkill';
import {
  filterCategoriesByKeyWord,
  getCategoryName,
  getCategoryValue,
} from '../utils/filterCategories';
import { filterSkillsByKeyWord } from '../utils/filterSkills';
import { FindExpertImageContainer } from '../SkillsWidgetsContainer/style/SkillsWidget.style';
import findExpertImage from 'src/assets/skillsFindExpertImage.png';
import { useGetSkillsByCategory } from 'src/tabs/hooks/useGetSkillsByCategory';
import { SkillBottomContainer } from './mobile/SkillBottomContainer';
import { SkillTopContainerMobile } from '../common/mobile/SkillTopContainerMobile';
import { ErrorsMessageContainer } from '../common/components/ErrorsMessageContainer';
import { ErrorsMessageContainerMobile } from '../common/mobile/ErrorsMessageContainerMobile';
import { SkillTopContainer } from '../common/components/SkillTopContainer';
import {
  SkillsAddBackdrop,
  HeaderDivider,
  SkillsBottomContainer,
  SelectorContainer,
  SmallAlertIcon,
  LabelStyled,
  RadioButtonWrapper,
  AddSkillDivider,
  SkillsDiscardButton,
  SkillsSaveButton,
  EditReadOnlyInput,
  RadioButtonStyled,
} from '../common/styles/common.styled';
import { appearance } from 'src/hooks/useAlertMessage';
import {
  SKILL_ADDED_SUCCESSFULLY_MESSAGE,
  SKILL_ADVANCED_MASTERY_LABEL,
  SKILL_ENTRY_LEVEL_MASTERY_LABEL,
  SKILL_INTERMEDIATE_MASTERY_LABEL,
} from 'src/tabs/constants/Skills.constants';
import { TeamsFxContext } from 'src/context/TeamsContext';

interface SkillsAddPopupProps {
  onClose: () => void;
  categories: OptionSet[];
  selectedCategoryOutsidePopup: string;
  employeeSkills: EmployeeSkillsByCategoryDTO[];
  existingSkillIds: string[];
  showAlert: (message: string, appearance: appearance) => void;
  getSkills: () => void;
}

export const SkillsAddPopup = ({
  onClose,
  categories,
  selectedCategoryOutsidePopup,
  employeeSkills,
  existingSkillIds,
  getSkills,
  showAlert,
}: SkillsAddPopupProps) => {
  const { skillsByCategory, handleCategoryChange } = useGetSkillsByCategory(
    selectedCategoryOutsidePopup
  );
  const { isMobile } = useContext(TeamsFxContext);
  const dropDownsRef = useRef(null);
  const [dropDownWidth, setDropDownWidth] = useState(0);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const { createSkill, loadingSkill } = useCreateSkill();
  const [searchSkillInput, setSearchSkillInput] = useState('');
  const [searchCategoryInput, setSearchCategoryInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(
    selectedCategoryOutsidePopup
  );

  const [selectedSkill, setSelectedSkill] = useState('');
  const [level, setLevel] = useState('');
  const [attitude, setAttitude] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const categoryName = getCategoryName(
    categories,
    selectedCategoryOutsidePopup
  );

  const categoryValue = getCategoryValue(categories, selectedCategory);

  const employeeSkillsCategory = employeeSkills.find(
    (skill) => skill.category.value === categoryValue
  );

  const filteredSkillsByCategory = skillsByCategory.filter((skill) => {
    return !employeeSkillsCategory?.employeeSkills.some(
      (employeeSkill) => employeeSkill.skill.id === skill.id
    );
  });

  const filteredCategoriesByKeyWord =
    searchCategoryInput && !selectedCategory
      ? filterCategoriesByKeyWord(categories, searchCategoryInput)
      : categories;

  const filteredSkillsByKeyWord = (
    searchSkillInput && !selectedSkill
      ? filterSkillsByKeyWord(filteredSkillsByCategory, searchSkillInput)
      : filteredSkillsByCategory
  ).filter((data) => !existingSkillIds.includes(data.id));

  const addSkillHalnder = async () => {
    if (level && attitude) {
      await createSkill(
        selectedSkill,
        level,
        attitude,
        categoryValue || selectedCategoryOutsidePopup
      );
      showAlert(SKILL_ADDED_SUCCESSFULLY_MESSAGE, 'success');
      onClose();
      getSkills();
    } else if (!level || !attitude) {
      if (!level && !attitude) {
        setErrorMessage('Please select your Level and Attitude to continue.');
      } else if (!level) {
        setErrorMessage('Please select your Level to continue.');
      } else if (!attitude) {
        setErrorMessage('Please select your Attitude to continue.');
      }
    }
  };

  const triggerRadioButton = (optionValue) => {
    const radioBtn = document.querySelector(
      `[value="${optionValue}"]`
    ) as HTMLInputElement;
    if (radioBtn) {
      radioBtn.click();
    }
  };

  const onClear = () => {
    if (!selectedCategoryOutsidePopup) {
      setSelectedCategory('');
    }
    setSearchSkillInput('');
    setSelectedSkill('');
    setAttitude('');
    setLevel('');
    setSearchCategoryInput('');
    setErrorMessage('');
  };

  useEffect(() => {
    document.addEventListener('keydown', detectKeyDown, true);
    const element = dropDownsRef.current;
    if (element) {
      setDropDownWidth(element.getBoundingClientRect().width);
    }
    const handleResize = () => {
      const newWindowSize = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      setWindowSize(newWindowSize);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [windowSize]);

  const detectKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.stopImmediatePropagation();
    }
  };

  return (
    <>
      {!isMobile && <SkillsAddBackdrop onClick={onClose} />}
      <SkillsAddPopupOverlay>
        {isMobile ? (
          <SkillTopContainerMobile
            onClear={onClear}
            onClose={onClose}
            title="Add a new skill"
            displayCondition={
              !selectedCategoryOutsidePopup
                ? selectedCategory !== ''
                : selectedSkill !== ''
            }
          />
        ) : (
          <SkillTopContainer onClose={onClose} title="Add a new skill" />
        )}
        <HeaderDivider />
        <SkillsMiddleContainer isExpanded={selectedSkill !== ''}>
          {errorMessage && (!level || !attitude) && (
            <ErrorsMessageContainerMobile
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
            />
          )}
          {selectedCategoryOutsidePopup ? (
            <EditReadOnlyInput value={categoryName} />
          ) : (
            <DropdownStyledPopup id="dropdown-id">
              <ComboboxStyle
                appearance="filled-darker"
                placeholder="Categories"
                onChange={(event) => {
                  const value = event.target.value;
                  setSearchCategoryInput(value);
                  if (selectedCategory && !selectedCategoryOutsidePopup) {
                    setSelectedCategory('');
                  }
                }}
                listbox={{
                  style: {
                    maxHeight: '250px',
                    width: `${dropDownWidth}px`,
                    fontSize: '16px',
                  },
                }}
                value={searchCategoryInput}
              >
                <OptionsWrapper>
                  {filteredCategoriesByKeyWord.map((category) => (
                    <Option
                      key={category.value}
                      onClick={() => {
                        if (selectedCategory) {
                          setLevel('');
                          setAttitude('');
                        }
                        setSelectedCategory(category.label);
                        handleCategoryChange(category.value);
                        setSearchCategoryInput(category.label);
                        setErrorMessage('');

                        if (!selectedCategoryOutsidePopup) {
                          setSearchSkillInput('');
                          setSelectedSkill('');
                        }
                      }}
                      checkIcon={{
                        style: {
                          color: '#5B5FC7',
                          opacity: selectedCategory === category.label ? 1 : 0,
                        },
                      }}
                    >
                      {category.label}
                    </Option>
                  ))}
                </OptionsWrapper>
              </ComboboxStyle>
            </DropdownStyledPopup>
          )}
          <DropdownStyledPopup ref={dropDownsRef} id="dropdown-id">
            <ComboboxStyle
              appearance="filled-darker"
              placeholder="Skill"
              onChange={(event) => {
                const value = event.target.value;
                setSearchSkillInput(value);
                if (selectedSkill) {
                  setSelectedSkill('');
                }
              }}
              listbox={{
                style: {
                  maxHeight: '250px',
                  width: `${dropDownWidth}px`,
                  fontSize: '16px',
                },
              }}
              value={searchSkillInput}
            >
              <OptionsWrapper>
                {filteredSkillsByKeyWord.map((skill) => (
                  <Option
                    key={skill.name}
                    onClick={() => {
                      if (selectedSkill) {
                        setLevel('');
                        setAttitude('');
                      }
                      setSearchSkillInput(skill.name);
                      setSelectedSkill(skill.id);
                      const currSkill = skillsByCategory.find(
                        (currSkill) => currSkill.id === skill.id
                      );
                      if (currSkill) {
                        setSelectedCategory(currSkill.category.label);
                        handleCategoryChange(currSkill.category.value);
                        setSearchCategoryInput(currSkill.category.label);
                      }
                      setErrorMessage('');
                    }}
                    checkIcon={{
                      style: {
                        color: '#5B5FC7',
                        opacity: selectedSkill === skill.name ? 1 : 0,
                      },
                    }}
                  >
                    {skill.name}
                  </Option>
                ))}
              </OptionsWrapper>
            </ComboboxStyle>
          </DropdownStyledPopup>
          {!selectedSkill && (
            <FindExpertImageContainer>
              <SelectASkillImageStyled
                src={findExpertImage}
                alt="Find an expert"
              />
              <h1>Add a new skill</h1>
              {!selectedCategory ? (
                <p>Get started by selecting skill category</p>
              ) : (
                <p>Please select the desired skill</p>
              )}
            </FindExpertImageContainer>
          )}
        </SkillsMiddleContainer>
        {selectedSkill && (
          <>
            {isMobile ? (
              <SkillBottomContainer
                attitude={attitude}
                errorMessage={errorMessage}
                level={level}
                selectedSkill={selectedSkill}
                setAttitude={setAttitude}
                setLevel={setLevel}
                triggerRadioButton={triggerRadioButton}
              />
            ) : (
              <SkillsBottomContainer>
                {errorMessage && (!level || !attitude) && (
                  <ErrorsMessageContainer errorMessage={errorMessage} />
                )}
                <SelectorContainer>
                  {errorMessage && !level && <SmallAlertIcon />}
                  <LabelStyled required>Level</LabelStyled>
                  <RadioGroupStyled
                    layout="horizontal"
                    value={level}
                    onChange={(_, newData) => setLevel(newData.value)}
                    disabled={!selectedSkill}
                  >
                    <Tooltip
                      content={SKILL_ENTRY_LEVEL_MASTERY_LABEL}
                      relationship="label"
                    >
                      <RadioButtonWrapper>
                        <RadioButtonStyled
                          errorStyle={errorMessage !== '' && !level}
                          value={Level.LEVEL1}
                          label="Level 1"
                        />
                      </RadioButtonWrapper>
                    </Tooltip>
                    <Tooltip
                      content={SKILL_INTERMEDIATE_MASTERY_LABEL}
                      relationship="label"
                    >
                      <RadioButtonWrapper>
                        <RadioButtonStyled
                          errorStyle={errorMessage !== '' && !level}
                          value={Level.LEVEL2}
                          label="Level 2"
                        />
                      </RadioButtonWrapper>
                    </Tooltip>
                    <Tooltip
                      content={SKILL_ADVANCED_MASTERY_LABEL}
                      relationship="label"
                    >
                      <RadioButtonWrapper>
                        <RadioButtonStyled
                          errorStyle={errorMessage !== '' && !level}
                          value={Level.LEVEL3}
                          label="Level 3"
                        />
                      </RadioButtonWrapper>
                    </Tooltip>
                  </RadioGroupStyled>
                </SelectorContainer>
                <AddSkillDivider />
                <SelectorContainer>
                  {errorMessage && !attitude && <SmallAlertIcon />}
                  <LabelStyled required>Attitude</LabelStyled>
                  <RadioGroupStyled
                    layout="horizontal"
                    value={attitude}
                    onChange={(_, newData) => setAttitude(newData.value)}
                    disabled={!selectedSkill}
                  >
                    <RadioButtonWrapper>
                      <RadioButtonStyled
                        errorStyle={errorMessage !== '' && !attitude}
                        value={Attitude.POSITIVE}
                        label="Positive"
                      />
                    </RadioButtonWrapper>
                    <RadioButtonWrapper>
                      <RadioButtonStyled
                        errorStyle={errorMessage !== '' && !attitude}
                        value={Attitude.NEUTRAL}
                        label="Neutral"
                      />
                    </RadioButtonWrapper>
                    <RadioButtonWrapper>
                      <RadioButtonStyled
                        errorStyle={errorMessage !== '' && !attitude}
                        value={Attitude.NEGATIVE}
                        label="Negative"
                      />
                    </RadioButtonWrapper>
                  </RadioGroupStyled>
                </SelectorContainer>
              </SkillsBottomContainer>
            )}
            <FinishingActionsContainer>
              {!isMobile && <p onClick={onClear}>Clear all</p>}
              <SkillsDiscardButton onClick={onClose}>
                Discard
              </SkillsDiscardButton>
              <SkillsSaveButton
                onClick={addSkillHalnder}
                disabled={loadingSkill}
              >
                Submit
              </SkillsSaveButton>
            </FinishingActionsContainer>
          </>
        )}
      </SkillsAddPopupOverlay>
    </>
  );
};
